import EventEmitter from 'eventemitter3';

import { Eip1193Provider } from './eip1193_provider';
import { Eip1193ProviderProxyErrorInfo } from './eip1193_provider_proxy_error_info';
import { Eip1193ProviderProxyClientMessageData } from './eip1193_provider_proxy_client_message_data';
import { Eip1193ProviderProxyServerMessageData } from './eip1193_provider_proxy_server_message_data';
import { Eip1193ProviderSendAsyncResponse } from './eip1193_provider_send_async_response';

export class Eip1193ProviderProxyBrowserWindowClient {
	_lastRequestPromiseId?: number;

	_requestPromisesCallbacks: {
		[requestPromiseId: number]: {
			resolve: (value: unknown) => void;
			reject: (error: Error) => void;
		};
	} = {};

	_lastSendAsyncCallbackId?: number;

	_sendAsyncCallbacks: {
		[sendAsyncCallbackId: number]: (error: Error | null, response: Eip1193ProviderSendAsyncResponse | null) => void;
	} = {};

	_eventEmitter: EventEmitter;

	_isDestroyed: boolean;

	_windowMessageEventListener: (windowMessageEvent: MessageEvent) => void;

	readonly provider: Readonly<Eip1193Provider>;

	constructor() {
		this._eventEmitter = new EventEmitter();

		this._isDestroyed = false;

		this._windowMessageEventListener = windowMessageEvent => {
			if (typeof windowMessageEvent.data !== `object`) {
				return;
			}

			const serverMessageData = windowMessageEvent.data.eip1193ProviderProxyServerMessageData as
				| Eip1193ProviderProxyServerMessageData
				| undefined;

			if (serverMessageData === undefined) {
				return;
			}

			this._onServerMessage(serverMessageData);
		};

		this.provider = {
			on: (...args: Array<any>) => (this._eventEmitter as any).on(...args),
			removeListener: (...args: Array<any>) => (this._eventEmitter as any).removeListener(...args),
			request: args => {
				const requestPromiseId = this._getNewRequestPromiseId();

				const requestPromise = new Promise((resolveRequestPromise, rejectRequestPromise) => {
					this._requestPromisesCallbacks[requestPromiseId] = {
						resolve: resolveRequestPromise,
						reject: rejectRequestPromise,
					};
				});

				this._sendMessage({
					invokeRequestMessageData: {
						requestPromiseId,
						args,
					},
				});

				return requestPromise;
			},
			sendAsync: (request, callback) => {
				const sendAsyncCallbackId = this._getNewSendAsyncCallbackId();

				this._sendAsyncCallbacks[sendAsyncCallbackId] = callback;

				this._sendMessage({
					invokeSendAsyncMessageData: {
						sendAsyncCallbackId,
						request,
					},
				});
			},
		};

		window.addEventListener(`message`, this._windowMessageEventListener);
	}

	destroy() {
		window.removeEventListener(`message`, this._windowMessageEventListener);

		this._isDestroyed = true;
	}

	_getNewRequestPromiseId() {
		const newRequestPromiseId = this._lastRequestPromiseId !== undefined ? this._lastRequestPromiseId + 1 : 0;

		this._lastRequestPromiseId = newRequestPromiseId;

		return newRequestPromiseId;
	}

	_getNewSendAsyncCallbackId() {
		const newSendAsyncCallbackId =
			this._lastSendAsyncCallbackId !== undefined ? this._lastSendAsyncCallbackId + 1 : 0;

		this._lastSendAsyncCallbackId = newSendAsyncCallbackId;

		return newSendAsyncCallbackId;
	}

	_createError(errorInfo: Eip1193ProviderProxyErrorInfo) {
		const error = new Error(errorInfo.message);

		if (errorInfo.code !== undefined) {
			Object.assign(error, { data: errorInfo.code });
		}

		if (errorInfo.data !== undefined) {
			Object.assign(error, { data: errorInfo.data });
		}

		return error;
	}

	_sendMessage(messageData: Eip1193ProviderProxyClientMessageData) {
		window.parent.postMessage(
			{
				eip1193ProviderProxyClientMessageData: messageData,
			},
			`*`
		);
	}

	_onServerMessage(serverMessageData: Eip1193ProviderProxyServerMessageData) {
		if (serverMessageData.onMessageListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(`message`, serverMessageData.onMessageListenerInvokedMessageData.message);
		} else if (serverMessageData.onConnectListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(`connect`, serverMessageData.onConnectListenerInvokedMessageData.info);
		} else if (serverMessageData.onDisconnectListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(
				`disconnect`,
				serverMessageData.onDisconnectListenerInvokedMessageData.errorInfo !== null
					? this._createError(serverMessageData.onDisconnectListenerInvokedMessageData.errorInfo)
					: null
			);
		} else if (serverMessageData.onChainChangedListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(`chainChanged`, serverMessageData.onChainChangedListenerInvokedMessageData.chainId);
		} else if (serverMessageData.onAccountsChangedListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(
				`accountsChanged`,
				serverMessageData.onAccountsChangedListenerInvokedMessageData.accounts
			);
		} else if (serverMessageData.requestPromiseFulfilledMessageData !== undefined) {
			const { requestPromiseId, requestPromiseResult, requestPromiseErrorInfo } =
				serverMessageData.requestPromiseFulfilledMessageData;

			if (requestPromiseErrorInfo === undefined) {
				this._requestPromisesCallbacks[requestPromiseId].resolve(requestPromiseResult);
			} else {
				this._requestPromisesCallbacks[requestPromiseId].reject(this._createError(requestPromiseErrorInfo));
			}

			delete this._requestPromisesCallbacks[requestPromiseId];
		} else if (serverMessageData.sendAsyncCallbackInvokedMessageData !== undefined) {
			const { sendAsyncCallbackId, sendAsyncCallbackResponse, sendAsyncCallbackErrorInfo } =
				serverMessageData.sendAsyncCallbackInvokedMessageData;

			const sendAsyncCallbackError =
				sendAsyncCallbackErrorInfo !== null ? this._createError(sendAsyncCallbackErrorInfo) : null;

			this._sendAsyncCallbacks[sendAsyncCallbackId](sendAsyncCallbackError, sendAsyncCallbackResponse);

			delete this._sendAsyncCallbacks[sendAsyncCallbackId];
		} else if (serverMessageData.onCloseListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(
				`close`,
				serverMessageData.onCloseListenerInvokedMessageData.errorInfo !== null
					? this._createError(serverMessageData.onCloseListenerInvokedMessageData.errorInfo)
					: null
			);
		} else if (serverMessageData.onChainIdChangedListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(
				`chainIdChanged`,
				serverMessageData.onChainIdChangedListenerInvokedMessageData.chainId
			);
		} else if (serverMessageData.onNetworkChangedListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(
				`networkChanged`,
				serverMessageData.onNetworkChangedListenerInvokedMessageData.networkId
			);
		} else if (serverMessageData.onNotificationListenerInvokedMessageData !== undefined) {
			this._eventEmitter.emit(`notification`, serverMessageData.onNotificationListenerInvokedMessageData.payload);
		}
	}
}
